import { useMemo, useState, type KeyboardEvent } from 'react';
import { z } from 'zod';
import { X } from 'lucide-react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { Input } from '@/components/ui/input';
import type {
    InfiniteScrollListFieldPredicateDescriptor,
    InfiniteScrollListJsonPathPredicateDescriptor,
} from '@/components/ui/infinite-scroll-list.types';
import { auditApi } from '@/lib/pillarApi';
import {
    TransactionLogTableNames,
    type ListTransactionLogPageResponse,
    type TransactionLogListItem,
    type TransactionLogSearchRequest,
    type TransactionLogTableName,
    type TransactionLogTablePrimaryKeyFilter,
} from '@pillar-portal/api-types';

interface TransactionLogRowProps {
    readonly item: TransactionLogListItem;
}

type PrimaryKeyValuesByTable = Partial<Record<TransactionLogTableName, string[]>>;
type PrimaryKeyInputByTable = Partial<Record<TransactionLogTableName, string>>;

const NoTableSelectionValue = '__audit_log_no_table_selection__';
const TransactionLogTableNameValues: ReadonlyArray<string> = TransactionLogTableNames;

const AuditLogFieldPredicateDescriptors: readonly InfiniteScrollListFieldPredicateDescriptor[] =
    [
        {
            field: 'operation',
            label: 'Operation',
            operator: 'in',
            valueType: 'string',
            options: [
                { value: 'INSERT', label: 'INSERT' },
                { value: 'UPDATE', label: 'UPDATE' },
                { value: 'DELETE', label: 'DELETE' },
            ],
        },
        {
            field: 'table_name',
            label: 'Table',
            operator: 'in',
            valueType: 'string',
            options: TransactionLogTableNames.map((tableName) => ({
                value: tableName,
                label: tableName,
            })),
        },
    ];

const AuditLogJsonPathPredicateDescriptors: readonly InfiniteScrollListJsonPathPredicateDescriptor[] =
    [{ field: 'table_row', label: 'Table row' }];

function isTransactionLogTableName(value: string): value is TransactionLogTableName {
    return TransactionLogTableNameValues.includes(value);
}

function splitPrimaryKeyInput(inputValue: string): string[] {
    return inputValue
        .split(',')
        .map((value: string) => value.trim())
        .filter((value: string) => value.length > 0);
}

function buildTablePrimaryKeyFilters(
    selectedTables: ReadonlyArray<TransactionLogTableName>,
    primaryKeyValuesByTable: PrimaryKeyValuesByTable
): TransactionLogTablePrimaryKeyFilter[] {
    return selectedTables.flatMap((tableName: TransactionLogTableName) => {
        const primaryKeyValues: string[] = primaryKeyValuesByTable[tableName] ?? [];
        if (primaryKeyValues.length === 0) {
            return [];
        }
        return [
            {
                table_name: tableName,
                primary_key_values: primaryKeyValues,
            },
        ];
    });
}

interface AuditLogFilterControlsProps {
    readonly selectedTables: ReadonlyArray<TransactionLogTableName>;
    readonly primaryKeyValuesByTable: PrimaryKeyValuesByTable;
    readonly primaryKeyInputByTable: PrimaryKeyInputByTable;
    readonly onAddTable: (tableName: TransactionLogTableName) => void;
    readonly onRemoveTable: (tableName: TransactionLogTableName) => void;
    readonly onPrimaryKeyInputChange: (
        tableName: TransactionLogTableName,
        value: string
    ) => void;
    readonly onAddPrimaryKeyValues: (tableName: TransactionLogTableName) => void;
    readonly onRemovePrimaryKeyValue: (
        tableName: TransactionLogTableName,
        primaryKeyValue: string
    ) => void;
}

function AuditLogChip({
    label,
    removeLabel,
    onRemove,
}: {
    readonly label: string;
    readonly removeLabel: string;
    readonly onRemove: () => void;
}) {
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-900">
            <span>{label}</span>
            <button
                type="button"
                aria-label={removeLabel}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={onRemove}
            >
                <X className="h-3 w-3" aria-hidden="true" />
            </button>
        </span>
    );
}

function AuditLogFilterControls(props: AuditLogFilterControlsProps) {
    const availableTables: TransactionLogTableName[] = TransactionLogTableNames.filter(
        (tableName: TransactionLogTableName) => !props.selectedTables.includes(tableName)
    );

    return (
        <div className="flex flex-1 flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <select
                    aria-label="Add audit table filter"
                    className="h-10 min-w-[260px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    value={NoTableSelectionValue}
                    onChange={(event) => {
                        const selectedValue = event.target.value;
                        if (selectedValue === NoTableSelectionValue) {
                            return;
                        }
                        if (!isTransactionLogTableName(selectedValue)) {
                            throw new Error(`Unknown audit-log table: ${selectedValue}`);
                        }
                        props.onAddTable(selectedValue);
                    }}
                >
                    <option value={NoTableSelectionValue}>Select audit table</option>
                    {availableTables.map((tableName: TransactionLogTableName) => (
                        <option key={tableName} value={tableName}>
                            {tableName}
                        </option>
                    ))}
                </select>
                <div className="flex flex-wrap gap-2">
                    {props.selectedTables.map((tableName: TransactionLogTableName) => (
                        <AuditLogChip
                            key={tableName}
                            label={tableName}
                            removeLabel={`Remove ${tableName} table filter`}
                            onRemove={() => props.onRemoveTable(tableName)}
                        />
                    ))}
                </div>
            </div>

            {props.selectedTables.length > 0 ? (
                <div className="space-y-2">
                    {props.selectedTables.map((tableName: TransactionLogTableName) => {
                        const primaryKeyValues: string[] =
                            props.primaryKeyValuesByTable[tableName] ?? [];
                        const inputValue: string =
                            props.primaryKeyInputByTable[tableName] ?? '';
                        return (
                            <div
                                key={tableName}
                                className="flex flex-wrap items-center gap-2"
                            >
                                <span className="w-56 text-xs font-medium text-gray-700">
                                    {tableName} Primary keys
                                </span>
                                <Input
                                    aria-label={`${tableName} Primary keys`}
                                    className="h-9 w-[220px]"
                                    value={inputValue}
                                    onChange={(event) =>
                                        props.onPrimaryKeyInputChange(
                                            tableName,
                                            event.target.value
                                        )
                                    }
                                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                                        if (event.key !== 'Enter' && event.key !== ',') {
                                            return;
                                        }
                                        event.preventDefault();
                                        props.onAddPrimaryKeyValues(tableName);
                                    }}
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => props.onAddPrimaryKeyValues(tableName)}
                                >
                                    Add
                                </Button>
                                <div className="flex flex-wrap gap-2">
                                    {primaryKeyValues.map((primaryKeyValue: string) => (
                                        <AuditLogChip
                                            key={primaryKeyValue}
                                            label={primaryKeyValue}
                                            removeLabel={`Remove ${primaryKeyValue} primary key from ${tableName}`}
                                            onRemove={() =>
                                                props.onRemovePrimaryKeyValue(
                                                    tableName,
                                                    primaryKeyValue
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

function TransactionLogRow({ item }: TransactionLogRowProps) {
    const ipAddress = item.ip_address ?? 'Unavailable';
    const userAgent = item.user_agent ?? 'Unavailable';

    return (
        <div className="border rounded-md p-3 bg-white space-y-2">
            <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">
                    {item.table_name} {item.operation}
                </div>
                <div className="text-xs text-gray-600">
                    ts={item.ts} ref={item.transaction_log_ref}
                </div>
                <div className="text-xs text-gray-600 truncate">
                    ip={ipAddress} ua={userAgent}
                </div>
            </div>

            <pre className="mt-2 text-xs bg-gray-50 p-3 rounded border overflow-x-auto max-h-96 overflow-y-auto">
                {JSON.stringify(item.table_row, null, 2)}
            </pre>
        </div>
    );
}

function AuditLogContent() {
    const [selectedTables, setSelectedTables] = useState<TransactionLogTableName[]>([]);
    const [primaryKeyValuesByTable, setPrimaryKeyValuesByTable] =
        useState<PrimaryKeyValuesByTable>({});
    const [primaryKeyInputByTable, setPrimaryKeyInputByTable] =
        useState<PrimaryKeyInputByTable>({});

    const tablePrimaryKeyFilters: TransactionLogTablePrimaryKeyFilter[] = useMemo(
        () => buildTablePrimaryKeyFilters(selectedTables, primaryKeyValuesByTable),
        [primaryKeyValuesByTable, selectedTables]
    );
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => [
            'auditLog',
            JSON.stringify({
                tables: selectedTables,
                table_primary_keys: tablePrimaryKeyFilters,
            }),
        ],
        [selectedTables, tablePrimaryKeyFilters]
    );

    return (
        <InfiniteScrollList<TransactionLogListItem, string, ReadonlyArray<string>>
            pageTitle="Audit Log"
            cardTitle="TransactionLog"
            queryPlaceholder="Search audit log..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={100}
            controlsPlacement="above-search"
            fetchPage={async (params) => {
                const request: TransactionLogSearchRequest = {
                    limit: params.limit,
                    ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    ...(selectedTables.length > 0 ? { tables: [...selectedTables] } : {}),
                    ...(tablePrimaryKeyFilters.length > 0
                        ? { table_primary_keys: tablePrimaryKeyFilters }
                        : {}),
                    ...(params.fieldPredicates !== undefined
                        ? { field_predicates: [...params.fieldPredicates] }
                        : {}),
                    ...(params.jsonPathPredicates !== undefined
                        ? { json_path_predicates: [...params.jsonPathPredicates] }
                        : {}),
                };
                const response: ListTransactionLogPageResponse =
                    await auditApi.listTransactionLogPage(request);
                return { items: response.transaction_log, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.string().min(1).safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected string cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading audit log...',
                emptyLabel: 'No audit log rows found.',
                endOfListLabel: 'End of audit log.',
                genericErrorLabel: 'Failed to load audit log',
            }}
            controls={
                <AuditLogFilterControls
                    selectedTables={selectedTables}
                    primaryKeyValuesByTable={primaryKeyValuesByTable}
                    primaryKeyInputByTable={primaryKeyInputByTable}
                    onAddTable={(tableName: TransactionLogTableName) => {
                        setSelectedTables((currentTables: TransactionLogTableName[]) => {
                            if (currentTables.includes(tableName)) {
                                return currentTables;
                            }
                            return [...currentTables, tableName];
                        });
                    }}
                    onRemoveTable={(tableName: TransactionLogTableName) => {
                        setSelectedTables((currentTables: TransactionLogTableName[]) =>
                            currentTables.filter(
                                (currentTableName: TransactionLogTableName) =>
                                    currentTableName !== tableName
                            )
                        );
                        setPrimaryKeyValuesByTable((currentValues) => {
                            const nextValues: PrimaryKeyValuesByTable = { ...currentValues };
                            delete nextValues[tableName];
                            return nextValues;
                        });
                        setPrimaryKeyInputByTable((currentValues) => {
                            const nextValues: PrimaryKeyInputByTable = { ...currentValues };
                            delete nextValues[tableName];
                            return nextValues;
                        });
                    }}
                    onPrimaryKeyInputChange={(
                        tableName: TransactionLogTableName,
                        value: string
                    ) => {
                        setPrimaryKeyInputByTable((currentValues) => ({
                            ...currentValues,
                            [tableName]: value,
                        }));
                    }}
                    onAddPrimaryKeyValues={(tableName: TransactionLogTableName) => {
                        setPrimaryKeyInputByTable((currentInputs) => {
                            const inputValue: string = currentInputs[tableName] ?? '';
                            const parsedPrimaryKeyValues: string[] =
                                splitPrimaryKeyInput(inputValue);
                            if (parsedPrimaryKeyValues.length === 0) {
                                return { ...currentInputs, [tableName]: '' };
                            }
                            setPrimaryKeyValuesByTable((currentValues) => {
                                const currentTableValues: string[] =
                                    currentValues[tableName] ?? [];
                                const nextTableValues: string[] = [...currentTableValues];
                                parsedPrimaryKeyValues.forEach((primaryKeyValue: string) => {
                                    if (!nextTableValues.includes(primaryKeyValue)) {
                                        nextTableValues.push(primaryKeyValue);
                                    }
                                });
                                return {
                                    ...currentValues,
                                    [tableName]: nextTableValues,
                                };
                            });
                            return { ...currentInputs, [tableName]: '' };
                        });
                    }}
                    onRemovePrimaryKeyValue={(
                        tableName: TransactionLogTableName,
                        primaryKeyValue: string
                    ) => {
                        setPrimaryKeyValuesByTable((currentValues) => {
                            const currentTableValues: string[] =
                                currentValues[tableName] ?? [];
                            return {
                                ...currentValues,
                                [tableName]: currentTableValues.filter(
                                    (currentPrimaryKeyValue: string) =>
                                        currentPrimaryKeyValue !== primaryKeyValue
                                ),
                            };
                        });
                    }}
                />
            }
            predicateControls={{
                fieldPredicates: AuditLogFieldPredicateDescriptors,
                jsonPathPredicates: AuditLogJsonPathPredicateDescriptors,
            }}
            renderItems={({ items }) => (
                <div className="space-y-6">
                    <p className="text-sm text-gray-600">
                        Transaction log rows in descending recency order.
                    </p>
                    <div className="space-y-3">
                        {items.map((item) => (
                            <TransactionLogRow key={item.transaction_log_ref} item={item} />
                        ))}
                    </div>
                </div>
            )}
        />
    );
}

export default function AuditLogPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <AuditLogContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
